import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { surveyApi } from '../lib/api'
import type {
  SaveSurveyGroupRequest,
  SaveSurveyQuestionRequest,
  SurveyQuestionSearchRequest,
} from '../types/survey'

// ========== Survey Groups ==========

export function useSurveyGroups() {
  return useQuery({
    queryKey: ['survey-groups'],
    queryFn: async () => {
      const response = await surveyApi.getAllGroups()
      return response.data
    },
  })
}

export function useSurveyGroupDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['survey-groups', id],
    queryFn: async () => {
      if (!id) throw new Error('Survey group ID is required')
      const response = await surveyApi.getGroupById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateSurveyGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveSurveyGroupRequest) =>
      surveyApi.createGroup(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-groups'] })
    },
  })
}

export function useUpdateSurveyGroup(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveSurveyGroupRequest) =>
      surveyApi.updateGroup(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['survey-groups', id], data)
      queryClient.invalidateQueries({ queryKey: ['survey-groups'] })
    },
  })
}

export function useDeleteSurveyGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => surveyApi.deleteGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-groups'] })
    },
  })
}

// ========== Survey Questions ==========

export function useSurveyQuestions() {
  return useQuery({
    queryKey: ['survey-questions'],
    queryFn: async () => {
      const response = await surveyApi.getAllQuestions()
      return response.data
    },
  })
}

export function useSurveyQuestionSearch(params: SurveyQuestionSearchRequest, enabled = true) {
  return useQuery({
    queryKey: ['survey-questions', 'search', params],
    queryFn: async () => {
      const response = await surveyApi.searchQuestions(params)
      return response.data
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useSurveyQuestionDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['survey-questions', id],
    queryFn: async () => {
      if (!id) throw new Error('Survey question ID is required')
      const response = await surveyApi.getQuestionById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateSurveyQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveSurveyQuestionRequest) =>
      surveyApi.createQuestion(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-questions'] })
      queryClient.invalidateQueries({ queryKey: ['survey-groups'] })
      queryClient.invalidateQueries({ queryKey: ['scripts', 'picker', 'questions'] })
    },
  })
}

export function useUpdateSurveyQuestion(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveSurveyQuestionRequest) =>
      surveyApi.updateQuestion(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['survey-questions', id], data)
      queryClient.invalidateQueries({ queryKey: ['survey-questions'] })
      queryClient.invalidateQueries({ queryKey: ['survey-groups'] })
      queryClient.invalidateQueries({ queryKey: ['scripts', 'picker', 'questions'] })
    },
  })
}

export function useDeleteSurveyQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => surveyApi.deleteQuestion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-questions'] })
      queryClient.invalidateQueries({ queryKey: ['survey-groups'] })
      queryClient.invalidateQueries({ queryKey: ['scripts', 'picker', 'questions'] })
    },
  })
}
